<!-- player/bookings/BookingNew/components/BookingAttendeesForm.vue -->
<template>
  <div class="booking-attendees-form">
    <div class="grid__row">
      <div class="grid__column grid__column--12 booking-attendees-form__header">
        <h3 class="heading--h3">Attendee Information</h3>
        <USButton
          type="inline"
          label="Add Another"
          icon="plus"
          @click="addGroup"
        />
      </div>
      <div class="grid__column grid__column--12 booking-attendees-form__blurb margin-bottom--6">
        <p class="body--p2--regular">
          We use attendee names and email addresses to send important updates about the event, build tee sheets and cart name tags, and help the host run a smooth check-in.
        </p>
      </div>
      <template v-for="(group, groupIdx) in groups" :key="`group-${groupIdx}`">
        <template v-for="(entry, entryIdx) in group.entries" :key="`group-${groupIdx}-entry-${entryIdx}`">
          <div
            class="grid__column grid__column--12 grid__column--breakpoint--7--6 booking-attendees-form__group margin-bottom--5"
            :class="{
              'booking-attendees-form__group--multi': group.entries.length > 1,
              'booking-attendees-form__group--multi-first': group.entries.length > 1 && entryIdx === 0,
              'booking-attendees-form__group--multi-spawned': group.entries.length > 1 && entryIdx > 0
            }"
          >
            <div class="booking-attendees-form__entry-content">
              <div class="booking-attendees-form__entry-header margin-bottom--7">
                <USStatusTag
                  v-if="group.entries.length > 1"
                  :label="entryLabel(groupIdx, entryIdx, group.entries.length)"
                  semantic="info"
                  class="booking-attendees-form__entry-tag"
                />
                <USStatusTag
                  v-else
                  label="Single Entry"
                  semantic="success"
                  class="booking-attendees-form__entry-tag"
                />
                <USButton
                  v-if="showDeleteForEntry(groupIdx, entryIdx)"
                  type="inline"
                  :label="removeGroupLabel(group.entries.length)"
                  class="booking-attendees-form__remove-group"
                  @click="removeGroup(groupIdx)"
                />
              </div>
              <div class="margin-bottom--7">
                <USInputSelect
                  :model-value="[group.admissionId]"
                  :options="admissionSelectOptions"
                  :disabled="group.entries.length > 1 && entryIdx > 0"
                  heading="Admission Type"
                  @update:modelValue="onAdmissionChange(groupIdx, $event)"
                />
              </div>
              <USInputText
                v-model="entry.firstName"
                class="margin-top--7"
                heading="First Name"
                :error="showErrors && !entry.firstName ? 'First name is required' : ''"
                @input="emitUpdate"
              />
              <USInputText
                v-model="entry.lastName"
                class="margin-top--7"
                heading="Last Name"
                :error="showErrors && !entry.lastName ? 'Last name is required' : ''"
                @input="emitUpdate"
              />
              <USInputText
                v-model="entry.email"
                class="margin-top--7"
                heading="Email"
                :error="showErrors && !entry.email ? 'Email is required' : ''"
                @input="emitUpdate"
              />
              <template v-if="customFields && customFields.length > 0">
                <template v-for="field in customFields" :key="field.id">
                  <USInputText
                    v-if="field.type === 'text'"
                    v-model="(entry.customFields[field.id] as string)"
                    class="margin-top--7"
                    :heading="field.label"
                    :error="showErrors && field.required && !entry.customFields[field.id] ? `${field.label} is required` : ''"
                    @input="emitUpdate"
                  />
                  <USInputNumber
                    v-else-if="field.type === 'number'"
                    v-model="(entry.customFields[field.id] as number)"
                    class="margin-top--7"
                    :heading="field.label"
                    :error="showErrors && field.required && (entry.customFields[field.id] === undefined || entry.customFields[field.id] === null || entry.customFields[field.id] === '') ? `${field.label} is required` : ''"
                    @input="emitUpdate"
                  />
                </template>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { useUserStore } from '@base/store/userStore'
import USButton from '@base/components/USButton/USButton.vue'
import USInputNumber from '@base/components/USInputNumber/USInputNumber.vue'
import USInputSelect from '@base/components/USInputSelect/USInputSelect.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'
import USStatusTag from '@base/components/USStatusTag/USStatusTag.vue'

export type AdmissionOption = {
  name: string
  label: string
  value: string
  attendees?: number
}

type CustomAttendeeField = {
  id: string
  label: string
  type: 'text' | 'number'
  required: boolean
  placeholder?: string | null
}

type EntryForm = {
  firstName: string
  lastName: string
  email: string
  customFields: { [fieldId: string]: string | number | undefined }
}

type Group = {
  admissionId: string
  entries: EntryForm[]
}

const userStore = useUserStore()

const props = defineProps<{
  admissions: AdmissionOption[]
  showErrors?: boolean
  customFields?: CustomAttendeeField[]
}>()

const emit = defineEmits<{
  (e: 'update', attendees: { id: string; firstName?: string; lastName?: string; email: string; customFields?: { [fieldId: string]: string } }[]): void
}>()

const defaultAdmissionId = () =>
  props.admissions?.length ? props.admissions[0].value : ''

const getAttendeesForAdmission = (admissionId: string) => {
  const opt = props.admissions?.find((a) => a.value === admissionId)
  return opt?.attendees ?? 1
}

const initializeCustomFields = (): { [fieldId: string]: string | number | undefined } => {
  const out: { [fieldId: string]: string | number | undefined } = {}
  if (props.customFields?.length) {
    for (const field of props.customFields) {
      out[field.id] = field.type === 'number' ? 0 : ''
    }
  }
  return out
}

const createEmptyEntry = (): EntryForm => ({
  firstName: userStore.firstName ?? '',
  lastName: userStore.lastName ?? '',
  email: userStore.email ?? '',
  customFields: initializeCustomFields()
})

const buildGroup = (admissionId: string): Group => {
  const count = Math.max(1, getAttendeesForAdmission(admissionId))
  const entries: EntryForm[] = []
  const first = createEmptyEntry()
  entries.push(first)
  for (let i = 1; i < count; i++) {
    entries.push({
      firstName: first.firstName,
      lastName: first.lastName,
      email: first.email,
      customFields: { ...first.customFields }
    })
  }
  return { admissionId, entries }
}

const groups = ref<Group[]>([buildGroup(defaultAdmissionId())])

const admissionSelectOptions = computed(() =>
  (props.admissions ?? []).map((a) => ({
    name: a.value,
    label: a.label,
    value: a.value
  }))
)

const entryLabel = (groupIdx: number, entryIdx: number, entriesInGroup: number) => {
  if (entriesInGroup > 1) {
    return `Team Entry ${entryIdx + 1}/${entriesInGroup}`
  }
  return ''
}

const removeGroupLabel = (entriesInGroup: number) =>
  entriesInGroup > 1 ? 'Remove Group' : 'Remove Entry'

const showDeleteForEntry = (_groupIdx: number, entryIdx: number) => entryIdx === 0

function onAdmissionChange(groupIdx: number, value: string[] | string) {
  const admissionId = Array.isArray(value) ? value[0] : value
  if (!admissionId) return
  const group = groups.value[groupIdx]
  if (!group) return
  const newCount = Math.max(1, getAttendeesForAdmission(admissionId))
  const newEntries: EntryForm[] = []
  for (let i = 0; i < newCount; i++) {
    if (i < group.entries.length) {
      newEntries.push({
        firstName: group.entries[i].firstName,
        lastName: group.entries[i].lastName,
        email: group.entries[i].email,
        customFields: { ...group.entries[i].customFields }
      })
    } else {
      newEntries.push({
        firstName: group.entries[0].firstName,
        lastName: group.entries[0].lastName,
        email: group.entries[0].email,
        customFields: { ...group.entries[0].customFields }
      })
    }
  }
  group.admissionId = admissionId
  group.entries = newEntries
  emitUpdate()
}

function addGroup() {
  groups.value.push(buildGroup(defaultAdmissionId()))
  emitUpdate()
}

function removeGroup(groupIdx: number) {
  groups.value.splice(groupIdx, 1)
  emitUpdate()
}

function flattenToAttendees(): { id: string; firstName?: string; lastName?: string; email: string; customFields?: { [fieldId: string]: string } }[] {
  const out: { id: string; firstName?: string; lastName?: string; email: string; customFields?: { [fieldId: string]: string } }[] = []
  for (const group of groups.value) {
    for (const entry of group.entries) {
      const customFields: { [fieldId: string]: string } = {}
      if (props.customFields?.length && entry.customFields) {
        for (const field of props.customFields) {
          const raw = entry.customFields[field.id]
          if (field.type === 'number') {
            if (raw !== undefined && raw !== null && raw !== '' && !(typeof raw === 'number' && isNaN(raw))) {
              customFields[field.id] = String(raw)
            } else if (field.required) {
              customFields[field.id] = ''
            }
          } else {
            const s = raw !== undefined && raw !== null && raw !== '' ? String(raw) : ''
            if (field.required || s) customFields[field.id] = s
          }
        }
      }
      const hasCustom = Object.keys(customFields).length > 0
      out.push({
        id: group.admissionId,
        firstName: entry.firstName || undefined,
        lastName: entry.lastName || undefined,
        email: entry.email,
        customFields: hasCustom ? customFields : undefined
      })
    }
  }
  return out
}

function emitUpdate() {
  emit('update', flattenToAttendees())
}

watch(
  () => props.admissions,
  (admissions) => {
    if (!admissions?.length || groups.value.length !== 1 || groups.value[0].entries.length !== 1) {
      return
    }
    const group = groups.value[0]
    const selectionValid = admissions.some((a) => a.value === group.admissionId)
    if (group.admissionId && selectionValid) return

    const fallbackId = admissions[0].value
    if (!fallbackId || group.admissionId === fallbackId) return

    groups.value[0] = buildGroup(fallbackId)
    emitUpdate()
  },
  { deep: true }
)

emitUpdate()
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';
@use '@base/styles/v1.0/scss/foundations/color';

.booking-attendees-form__header {
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  gap: var(--spacing--4);
  justify-content: space-between;
  margin-bottom: var(--spacing--6);
}

.booking-attendees-form__blurb {
  color: var(--color--neutral-a3);

  p {
    margin: 0;
  }
}

.booking-attendees-form__entry-header {
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  gap: var(--spacing--6);
  width: 100%;
}

.booking-attendees-form__entry-tag {
  flex: 0 1 auto;
}

.booking-attendees-form__remove-group {
  flex-shrink: 0;
  margin-left: auto;
}

.booking-attendees-form__group--multi-first {
  padding-bottom: var(--spacing--6);
}

.input--error {
  input {
    border-color: var(--color--semantic--error-2) !important;
  }
}

.error {
  color: var(--color--semantic--error-2);
  padding-top: var(--spacing--8);
}
</style>
